import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { notification, Progress, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import apiService from '../services/apiService';

const STORAGE_KEY = 'excelExportJobId';
const NOTIF_KEY = 'excel-export';

const ExportContext = createContext(null);

const getProgressMessage = (progress) => {
  if (!progress) return '';
  const { stage, processed = 0, total = 0, error } = progress;
  switch (stage) {
    case 'queued': return 'Export queued on the server...';
    case 'querying': return `Querying invoices (${processed.toLocaleString()} of ${total.toLocaleString()})`;
    case 'query_retrying': return error || `Retrying query...`;
    case 'mapping': return `Processing invoices (${processed.toLocaleString()} of ${total.toLocaleString()})`;
    case 'building_workbook': return 'Building Excel workbook...';
    case 'finalizing_file': return 'Finalizing Excel file...';
    case 'ready': case 'startingDownload': return 'Ready to download...';
    case 'complete': return `Export complete${total ? ` (${total.toLocaleString()} invoices)` : ''}.`;
    case 'cancelled': return 'Export cancelled.';
    case 'error': return error || 'Export failed.';
    default: return 'Preparing export...';
  }
};

export const ExportProvider = ({ children }) => {
  const pollingRef = useRef(null);
  const activeJobIdRef = useRef(null);

  const stopPolling = useCallback(() => {
    clearInterval(pollingRef.current);
    pollingRef.current = null;
    activeJobIdRef.current = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const showNotification = useCallback((progress, jobId) => {
    if (!progress) return;
    const { stage, percent = 0 } = progress;
    const isDone = stage === 'complete';
    const isFailed = stage === 'error' || stage === 'cancelled';

    notification.open({
      key: NOTIF_KEY,
      message: isDone ? 'Export complete' : isFailed ? 'Export stopped' : 'Exporting to Excel',
      description: (
        <div>
          <div style={{ marginBottom: 6, fontSize: 12, color: '#595959' }}>
            {getProgressMessage(progress)}
          </div>
          {!isFailed && (
            <Progress
              percent={Math.round(percent)}
              size="small"
              status={isDone ? 'success' : 'active'}
            />
          )}
          {!isDone && !isFailed && jobId && (
            <Button
              size="small"
              danger
              style={{ marginTop: 8 }}
              onClick={() => {
                apiService.invoices.cancelInvoiceExportJob(jobId).catch(() => {});
                stopPolling();
                notification.destroy(NOTIF_KEY);
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      ),
      duration: isDone || isFailed ? 8 : 0,
      placement: 'bottomRight',
      style: { width: 340, minWidth: 340 },
      icon: isDone
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : isFailed
          ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
          : <LoadingOutlined style={{ color: '#1677ff' }} />,
      onClose: isDone || isFailed ? stopPolling : undefined,
    });
  }, [stopPolling]);

  // Start polling a resumed job from sessionStorage (page refresh scenario)
  const startResumePolling = useCallback((jobId) => {
    if (activeJobIdRef.current === jobId) return;
    clearInterval(pollingRef.current);
    activeJobIdRef.current = jobId;

    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiService.invoices.getInvoiceExportJob(jobId);
        const job = res?.data;
        if (!job) return;

        const stage = job.status === 'completed' ? 'complete'
          : job.status === 'failed' ? 'error'
          : job.status === 'cancelled' ? 'cancelled'
          : job.currentPhase || job.status || 'queued';

        const progress = {
          stage,
          percent: job.percent || 0,
          processed: job.progressCurrent || 0,
          total: job.progressTotal || 0,
          error: job.errorMessage || null
        };

        showNotification(progress, jobId);

        if (stage === 'complete' || stage === 'error' || stage === 'cancelled') {
          stopPolling();
          if (stage === 'complete') {
            try {
              const blob = await apiService.invoices.downloadInvoiceExportJob(jobId);
              const blobData = blob?.data instanceof Blob ? blob.data : blob;
              const url = URL.createObjectURL(blobData);
              const link = document.createElement('a');
              link.href = url;
              link.download = job.fileName || 'invoices_export.xlsx';
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch {
              // Download silently failed — job complete notification still shows
            }
          }
        }
      } catch {
        // Transient poll error — keep retrying
      }
    }, 1500);
  }, [showNotification, stopPolling]);

  // On mount: resume polling for any job that was active before a page refresh
  useEffect(() => {
    const savedJobId = sessionStorage.getItem(STORAGE_KEY);
    if (savedJobId) startResumePolling(savedJobId);
    return () => clearInterval(pollingRef.current);
  }, [startResumePolling]);

  // Called by InvoicesList when a job is first created
  const trackExportJob = useCallback((jobId) => {
    if (!jobId) return;
    sessionStorage.setItem(STORAGE_KEY, jobId);
    activeJobIdRef.current = jobId;
  }, []);

  // Called by InvoicesList onProgress to update the global notification
  const reportExportProgress = useCallback((progress) => {
    const jobId = activeJobIdRef.current;
    showNotification(progress, jobId);
    if (!progress || progress.stage === 'complete' || progress.stage === 'error' || progress.stage === 'cancelled') {
      stopPolling();
    }
  }, [showNotification, stopPolling]);

  // Called by InvoicesList on cleanup (export done/cancelled)
  const clearExport = useCallback(() => {
    stopPolling();
    notification.destroy(NOTIF_KEY);
  }, [stopPolling]);

  return (
    <ExportContext.Provider value={{ trackExportJob, reportExportProgress, clearExport }}>
      {children}
    </ExportContext.Provider>
  );
};

export const useExportContext = () => useContext(ExportContext);
